import * as THREE from 'three';
import { makeMeatDrop, spawnCrashCoastNodes, type GatherNode } from '../gather/Nodes';
import { Grid, HOTBAR_SLOT, isHoldable } from '../inventory/Grid';
import { setInvHud, setPrompt } from '../ui/Prompt';
import {
  nearestInteract,
  queryInteract,
  type InteractHit,
  type Xyz,
} from './Interact';

const _look = new THREE.Vector3();

export type PickupHook = {
  held: string | null;
  inventory: { id: string; count: number }[];
  prompt: string;
};

export class PickupDirector {
  readonly inventory = new Grid();
  held: string | null = null;
  prompt = '';
  lastTake = '';
  private readonly nodes: GatherNode[];
  private lit: GatherNode | null = null;
  private meatSeq = 1;

  constructor(private readonly scene: THREE.Scene) {
    this.nodes = spawnCrashCoastNodes(scene);
  }

  /** World pile the player can E. Always raw — never cooked. */
  dropRaw(pos: Xyz, qty = 1): void {
    const n = Math.max(1, Math.floor(qty));
    const node = makeMeatDrop(pos, n, `RAW-${this.meatSeq}`);
    this.meatSeq += 1;
    this.scene.add(node.mesh);
    this.nodes.push(node);
    this.lastTake = n > 1 ? `Raw meat ×${n}` : 'Raw meat';
  }

  tick(camera: THREE.Camera, player: Xyz, use: boolean): boolean {
    const hit = this.query(camera, player);
    this.syncPrompt(hit);
    if (use) {
      const ok = this.use(hit);
      this.syncPrompt(this.query(camera, player));
      return ok;
    }
    return hit !== null;
  }

  /** Look-cone query. Used by the live E press. */
  query(camera: THREE.Camera, player: Xyz): InteractHit | null {
    camera.getWorldDirection(_look);
    return queryInteract(player, _look, player, this.nodes) ?? this.nearest(player);
  }

  nearest(player: Xyz): InteractHit | null {
    return nearestInteract(player, this.nodes);
  }

  /** QA hook: current look hit, else nearest in 2.2 m. */
  forceUse(camera: THREE.Camera, player: Xyz): boolean {
    const hit = this.query(camera, player) ?? this.nearest(player);
    const ok = this.use(hit);
    this.syncPrompt(this.query(camera, player) ?? this.nearest(player));
    return ok;
  }

  use(hit: InteractHit | null): boolean {
    const node = hit?.target as GatherNode | undefined;
    if (!node || !node.live) {
      return false;
    }
    if (node.kind === 'spear') {
      const ok = this.takeSpear(node);
      if (ok) {
        this.lastTake = 'Improvised spear';
      }
      return ok;
    }
    if (!node.take()) {
      return false;
    }
    this.inventory.add(node.item, node.qty);
    this.lastTake =
      node.kind === 'raw'
        ? node.qty > 1
          ? `Raw meat ×${node.qty}`
          : 'Raw meat'
        : node.kind === 'fiber'
          ? `Fiber ×${node.qty}`
          : `Wood ×${node.qty}`;
    return true;
  }

  give(id: string, n = 1): void {
    this.inventory.add(id, n);
    if (isHoldable(id)) {
      this.inventory.equip(id, HOTBAR_SLOT);
      this.held = id;
    }
  }

  equipSlot(slot: number): boolean {
    const id = this.inventory.held(slot);
    if (!id) {
      return false;
    }
    this.held = id;
    return true;
  }

  restore(stacks: { id: string; count: number }[]): void {
    this.inventory.replace(stacks);
    const hot = this.inventory.held();
    if (hot && isHoldable(hot)) {
      this.held = hot;
    } else if (this.inventory.countOf('spear') > 0) {
      this.inventory.equip('spear', HOTBAR_SLOT);
      this.held = 'spear';
    } else if (this.inventory.countOf('bow') > 0) {
      this.inventory.equip('bow', HOTBAR_SLOT);
      this.held = 'bow';
    } else {
      this.held = hot;
    }
  }

  publish(hook: PickupHook): void {
    hook.held = this.held;
    hook.inventory = this.inventory.stacks();
    hook.prompt = this.prompt;
    setInvHud(hook.inventory);
  }

  private takeSpear(node: GatherNode): boolean {
    if (!node.live) {
      return false;
    }
    for (const other of this.nodes) {
      if (other.kind === 'spear' && other.live) {
        other.take();
      }
    }
    this.inventory.equip('spear', HOTBAR_SLOT);
    this.held = 'spear';
    return true;
  }

  private syncPrompt(hit: InteractHit | null): void {
    this.prompt = hit?.target.prompt ?? '';
    setPrompt(this.prompt);
    const node = (hit?.target as GatherNode | undefined) ?? null;
    if (this.lit === node) {
      return;
    }
    glow(this.lit, false);
    glow(node, true);
    this.lit = node;
  }
}

function glow(node: GatherNode | null, on: boolean): void {
  if (!node) {
    return;
  }
  node.mesh.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) {
      return;
    }
    const mat = obj.material;
    if (mat instanceof THREE.MeshStandardMaterial) {
      mat.emissive.setHex(on ? 0x4a3a20 : 0x000000);
      mat.emissiveIntensity = on ? 0.55 : 0;
    }
  });
}
